// Board values are configuration, never code (standing build rule 15; architecture plan §8.5).
// Epic ids, area paths, autonomy levels and the board hold are read at runtime by the
// deterministic applier in `events`. The agent never reads any of this — it always
// investigates and drafts, and the applier decides whether the proposal may be written
// (§8.3, §8.10). Keeping the shape here, in contracts, is what makes "the agent does not
// read configuration" checkable: nothing in `apps/triage` imports this schema.

import { z } from "zod";
import { AutonomyLevelSchema, StateCategorySchema } from "./enums";

/**
 * One state as it actually exists on this project's process, with its category.
 *
 * Both halves preserve the board's actual metadata. Customer confirmation can additionally
 * use the type's explicit confirmationState when the board categorizes it as InProgress.
 */
export const BoardStateSchema = z.object({
  /** The literal state name on the board, e.g. "Active". Used when writing. */
  name: z.string().min(1),
  /** The actual Azure Boards category; never relabel it to express portal confirmation. */
  category: StateCategorySchema,
});
export type BoardState = z.infer<typeof BoardStateSchema>;

/**
 * A legal transition, expressed **by category** (§9.4). Recording transitions by name would
 * re-introduce exactly the process-template coupling the category abstraction removes.
 */
export const BoardStateTransitionSchema = z.object({
  from: StateCategorySchema,
  to: StateCategorySchema,
});
export type BoardStateTransition = z.infer<typeof BoardStateTransitionSchema>;

/**
 * A configured work-item type: the states Phase 0 observed on the live process, their
 * categories, and the transitions between those categories (build plan Phase 0 —
 * "Phase 0 discovers and records, per configured work-item type, the actual states, their
 * categories, and the legal transitions between them", §9.4).
 */
export const WorkItemTypeConfigSchema = z
  .object({
    /** The type name as the board spells it, e.g. "User Story". */
    name: z.string().min(1),
    /** The type's reference name when the process exposes one, e.g. "Microsoft.VSTS.WorkItemTypes.UserStory". */
    referenceName: z.string().min(1).optional(),
    states: z.array(BoardStateSchema).min(1),
    /** Exact existing state that awaits customer confirmation, when its category is InProgress. */
    confirmationState: z.string().min(1).optional(),
    transitions: z.array(BoardStateTransitionSchema).default([]),
  })
  .refine(
    (type) => new Set(type.states.map((s) => s.name)).size === type.states.length,
    "state names must be unique within a work-item type",
  )
  .refine(
    (type) =>
      type.confirmationState === undefined ||
      type.states.some(
        (state) =>
          state.name === type.confirmationState &&
          (state.category === "InProgress" || state.category === "Resolved"),
      ),
    "confirmationState must name an existing InProgress or Resolved state",
  )
  .refine(
    (type) =>
      type.confirmationState === undefined ||
      type.states.some(
        (state) => state.category === "InProgress" && state.name !== type.confirmationState,
      ),
    "confirmationState requires another InProgress state for reopening",
  )
  .refine((type) => {
    const present = new Set(type.states.map((s) => s.category));
    return type.transitions.every((t) => present.has(t.from) && present.has(t.to));
  }, "a transition names a state category this type does not have");
export type WorkItemTypeConfig = z.infer<typeof WorkItemTypeConfigSchema>;

/** True when this type has a native Azure Boards Resolved category. */
export function hasResolvedCategory(type: WorkItemTypeConfig): boolean {
  return type.states.some((s) => s.category === "Resolved");
}

/**
 * The types the integration writes. `request` is the **primary** type — the one a requester's
 * status follows (§9.7) — and `task` is the child-Task type used when work is shaped (§8.7).
 * `story` and `bug` are optional because a project may use the primary type for both.
 */
export const WorkItemTypesConfigSchema = z.object({
  request: WorkItemTypeConfigSchema,
  task: WorkItemTypeConfigSchema,
  story: WorkItemTypeConfigSchema.optional(),
  bug: WorkItemTypeConfigSchema.optional(),
});
export type WorkItemTypesConfig = z.infer<typeof WorkItemTypesConfigSchema>;

/**
 * Access planes (§8.5). The plane a request needs determines its Epic parent and the
 * prerequisites the card must state; it is not cosmetic.
 */
export const EPIC_PARENT_PLANES = ["cloud", "internal", "onboarding"] as const;
export const EpicParentPlaneSchema = z.enum(EPIC_PARENT_PLANES);
export type EpicParentPlane = z.infer<typeof EpicParentPlaneSchema>;

/**
 * Epic parent per access plane (§8.5, standing build rule 15). The live values change; they
 * belong in `config/board.example.json` and the Phase 0 worksheet, never in a source file.
 */
export const EpicParentsSchema = z.object({
  cloud: z.number().int().positive(),
  internal: z.number().int().positive(),
  onboarding: z.number().int().positive(),
});
export type EpicParents = z.infer<typeof EpicParentsSchema>;

/**
 * The exact-match key used for replay protection before any create (§8.4, §8.8, §19).
 *
 * The custom field is the decision of record. The tag fallback exists only for a project whose
 * inherited process cannot be customized, and it is accepted knowing it adds one permanent,
 * single-use tag to the shared tag list per request.
 */
export const ExactMatchKeySchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("customField"),
    /** Reference name of the custom field, e.g. "Custom.OPSupportRequestId" (§8.8). */
    referenceName: z.string().min(1),
  }),
  z.object({ kind: z.literal("tag") }),
]);
export type ExactMatchKey = z.infer<typeof ExactMatchKeySchema>;

/** Tags applied to every card the integration creates or reuses (§8.8). */
export const BoardTagsSchema = z.object({
  /** Identifies portal-originated work. */
  supportTag: z.string().min(1).default("OP-Support"),
  /** The board standard's date tag form. Tokens only — a literal date here is a mistake. */
  dateTagFormat: z
    .string()
    .regex(/^[YMD\-/.]+$/, "date tag format uses only Y, M, D and separators")
    .default("YYYY-MM-DD"),
});
export type BoardTags = z.infer<typeof BoardTagsSchema>;

/**
 * Per-category autonomy (§8.12, §13.2). Every category starts at L1 — draft only, a human
 * promotes — and graduates on numeric held-out evidence, never on self-assessed confidence.
 * `boardHold` is the restructure hold from Board Sync §9: honoured by the applier, not the
 * agent (§8.3).
 */
export const AutonomyConfigSchema = z.object({
  /** Applied to any category without an explicit entry. L1 by default. */
  default: AutonomyLevelSchema.default("L1"),
  /** Request category → level. Admin-changeable at runtime, without a deployment (§13.2). */
  byCategory: z.record(z.string().min(1), AutonomyLevelSchema).default({}),
});
export type AutonomyConfig = z.infer<typeof AutonomyConfigSchema>;

/**
 * The behaviour surface, pinned as one release artifact (§13.2). Prompt text, model identifier
 * and version, tool definitions, the wiki corpus hash and the validator set ship together and
 * get the same code review as `packages/boardstd` (standing build rule 11). Reviewing the
 * validators while the prompt or the corpus moves freely leaves most of the behaviour
 * ungoverned, so these are one object and not five loose keys.
 */
export const BehaviourSurfaceVersionSchema = z.object({
  /** The release tag covering everything below, recorded on every triage run. */
  version: z.string().min(1),
  modelId: z.string().min(1),
  modelVersion: z.string().min(1),
  promptVersion: z.string().min(1),
  toolDefinitionsVersion: z.string().min(1),
  /** Commit or content hash of the searchable wiki corpus (§8.6). */
  wikiCorpusHash: z.string().min(1),
  /** Version of the `packages/boardstd` validator set the gate runs. */
  validatorSetVersion: z.string().min(1),
});
export type BehaviourSurfaceVersion = z.infer<typeof BehaviourSurfaceVersionSchema>;

/**
 * Everything the applier and the reconciler read at runtime. Provisioned from SSM Parameter
 * Store. Separate reader and writer principals are scoped to the selected project; deployment
 * additionally checks this project identity against the public runtime configuration.
 */
export const BoardConfigSchema = z
  .object({
    /** e.g. "https://dev.azure.com/opsolutions". */
    organizationUrl: z.url(),
    project: z.object({
      name: z.string().min(1),
      /** The project GUID. Checked against the org at startup; a mismatch is a hard failure. */
      id: z.guid(),
    }),
    /**
     * The restricted support area path (§9.3). Set on every item the integration creates,
     * including child Tasks under human-created parents, so requester PII never lands in the
     * generally readable area.
     */
    supportAreaPath: z.string().min(1),
    /** Current iteration or the backlog (§19). */
    iterationPath: z.string().min(1),
    workItemTypes: WorkItemTypesConfigSchema,
    epicParents: EpicParentsSchema,
    exactMatchKey: ExactMatchKeySchema,
    tags: BoardTagsSchema,
    /**
     * Namespace for every automated comment marker (standing build rule 4). Must match the
     * namespace `commentMarker()` in `packages/ado` emits, or markers written by one release
     * are invisible to the next and duplicate comments follow.
     */
    commentMarkerNamespace: z
      .string()
      .regex(/^[a-z0-9-]+$/, "marker namespace is lowercase, digits and hyphens")
      .default("op-support"),
    autonomy: AutonomyConfigSchema,
    /** Global restructure hold. While true the applier holds every proposal (§8.3). */
    boardHold: z.boolean().default(false),
    behaviourSurfaceVersion: BehaviourSurfaceVersionSchema,
  })
  .refine(
    (config) =>
      hasResolvedCategory(config.workItemTypes.request) ||
      config.workItemTypes.request.confirmationState !== undefined,
    // Existing User Stories may put their confirmation state in InProgress. Require an
    // explicit, validated mapping in that case; state names never imply confirmation alone.
    {
      error:
        "the request (primary) work-item type must have a Resolved category or explicit confirmationState",
      path: ["workItemTypes", "request", "states"],
    },
  )
  .refine((config) => config.workItemTypes.request.name.toLowerCase() !== "task", {
    // §8.7: the primary is never a Task. A Task cannot carry a requester's status.
    error: "the request (primary) work-item type must not be a Task (§8.7)",
    path: ["workItemTypes", "request", "name"],
  });
export type BoardConfig = z.infer<typeof BoardConfigSchema>;

/** The level that applies to a category, falling back to the configured default (§8.12). */
export function autonomyFor(config: BoardConfig, category: string) {
  return config.autonomy.byCategory[category] ?? config.autonomy.default;
}
